import {useEffect,useState} from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {ContactContentSkeleton} from '../components/PageSkeletons';
import {SkeletonBlock,SkeletonImage,SkeletonTextLines} from '../components/SkeletonPrimitives';
import {fetchContactPageScalars,fetchGlobalSettingsScalars,mapContactPageScalars,mapGlobalSettingsScalars} from '../services/wordpress';

const {FiArrowUpRight,FiCheck}=FiIcons;
const contactFallback={eyebrow:'Contact',heading:"Let's talk about your project.",description:"Whether you're planning a new space,renovation or interior project,we'd love to hear what you have in mind.",contactImage:'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=1400&q=90',imageCaption:'FORMA Studio · New York'};
const globalFallback={brandMark:'F',brandName:'FORMA',brandDescriptor:'Studio',headerCtaLabel:'Start a Project',headerCtaUrl:'/contact',studioAddress:'125 Mercer Street\nNew York,NY 10012',contactEmail:'hello@formastudio.com',contactPhone:'+1 212 555 0148',footerDescription:'Architecture shaped around life.',footerHeading:"Let's make space\nfor what matters.",copyrightText:'© 2025 FORMA Studio. All rights reserved.',footerLocation:'New York · Everywhere'};

function renderMultiline(value) {
  return value.split(/\r?\n/).map((line,index) => (
    <span key={`${line}-${index}`}>{index > 0 && <br />}{line}</span>
  ));
}

function ContactForm({sent,setSent}) {
  function handleSubmit(event) {
    event.preventDefault();
    setSent(true);
    event.currentTarget.reset();
  }

  return (
    <form className="contact-form" onSubmit={handleSubmit}>
      <div className="form-row">
        <label>Name<input type="text" name="name" required /></label>
        <label>Email<input type="email" name="email" required /></label>
      </div>
      <div className="form-row">
        <label>Phone<input type="tel" name="phone" /></label>
        <label> Project Type <select name="projectType" defaultValue="" required>
          <option value="" disabled>Select one</option>
          <option>Architecture</option>
          <option>Interior Design</option>
          <option>Renovation</option>
          <option>Space Planning</option>
          <option>Design Consultation</option>
        </select></label>
      </div>
      <label>Message<textarea name="message" rows="5" required /></label>
      <button className="button button-dark" type="submit">
        {sent ? <>Enquiry Sent <SafeIcon icon={FiCheck} /></> : <>Send Enquiry <SafeIcon icon={FiArrowUpRight} /></>}
      </button>
      {sent && <p className="form-success">Thank you. Your enquiry has been prepared successfully. This frontend-only form does not send or store submissions yet.</p>}
    </form>
  );
}

function ContactDetails({content}) {
  return (
    <div className="contact-details">
      <div><span className="footer-label">Email</span><a href={`mailto:${content.contactEmail}`}>{content.contactEmail}</a></div>
      <div><span className="footer-label">Phone</span><a href={`tel:${content.contactPhone.replace(/[^\d+]/g,'')}`}>{content.contactPhone}</a></div>
      <div><span className="footer-label">Studio</span><p>{renderMultiline(content.studioAddress)}</p></div>
    </div>
  );
}

export default function Contact() {
  const [content,setContent]=useState(null);
  const [globalContent,setGlobalContent]=useState(null);
  const [status,setStatus]=useState({contact:'loading',global:'loading'});
  const [sent,setSent]=useState(false);

  useEffect(() => {
    let active=true;
    fetchContactPageScalars().then((meta) => {
      if (active) {
        setContent(mapContactPageScalars(meta,contactFallback));
        setStatus((current) => ({...current,contact:'success'}));
      }
    }).catch(() => {
      if (active) {
        setContent(contactFallback);
        setStatus((current) => ({...current,contact:'error'}));
      }
    });
    fetchGlobalSettingsScalars().then((meta) => {
      if (active) {
        setGlobalContent(mapGlobalSettingsScalars(meta,globalFallback));
        setStatus((current) => ({...current,global:'success'}));
      }
    }).catch(() => {
      if (active) {
        setGlobalContent(globalFallback);
        setStatus((current) => ({...current,global:'error'}));
      }
    });
    return () => {active=false;};
  },[]);

  const loading=status.contact==='loading' || status.global==='loading';
  const pageContent=content || contactFallback;
  const sharedContent=globalContent || globalFallback;

  return (
    <div className="contact-page page-width">
      {loading ? (
        <ContactContentSkeleton />
      ) : (
        <>
          <div className="contact-intro">
            <span className="eyebrow">{pageContent.eyebrow}</span>
            <h1>{pageContent.heading}</h1>
            <p>{pageContent.description}</p>
            <ContactDetails content={sharedContent} />
          </div>
          <div className="contact-image">
            <img src={pageContent.contactImage} alt="Refined architectural interior" />
            <span>{pageContent.imageCaption}</span>
          </div>
        </>
      )}
      <div className="contact-layout">
        <ContactForm sent={sent} setSent={setSent} />
        {!loading && null}
      </div>
    </div>
  );
}